import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { toast } from "sonner";
import { Robot, TrendUp, TrendDown, Sparkle } from "@phosphor-icons/react";

const MODES = ["scalp", "swing", "position", "macro"];

// Colored bar for edge visualization (edge_pp positive = green, negative = red).
function EdgeBar({ pp, max = 30 }) {
  const clamped = Math.max(-max, Math.min(max, pp));
  const width = (Math.abs(clamped) / max) * 100;
  const isPos = clamped >= 0;
  return (
    <div className="relative h-2 w-16 bg-white/5 border border-white/10">
      <div
        className="absolute top-0 h-full"
        style={{
          width: `${width}%`,
          background: isPos ? "#00FF66" : "#FF3B30",
          left: isPos ? "50%" : `${50 - width / 2}%`,
        }}
      />
      <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white/40" />
    </div>
  );
}

export function WinnerProfilePanel() {
  const [nPairs, setNPairs] = useState(20);
  const [days, setDays] = useState(60);

  // Poll the result endpoint every 5s so we automatically pick up the completion.
  const status = useQuery({
    queryKey: ["winner-profile"],
    queryFn: api.winnerProfileResult,
    refetchInterval: 5_000,
  });

  const start = useMutation({
    mutationFn: () => api.winnerProfileStart(nPairs, days),
    onSuccess: (data) => {
      toast.info(data.message || "Analysis started");
      status.refetch();
    },
    onError: () => toast.error("Failed to start analysis"),
  });

  const jobStatus = status.data?.status || "idle";
  const isRunning = jobStatus === "running";
  const result = status.data?.result;

  const overrides = useQuery({
    queryKey: ["mode-overrides"],
    queryFn: api.modeOverrides,
    refetchInterval: 30_000,
  });
  const qc = useQueryClient();
  const applyWeights = useMutation({
    mutationFn: api.applySuggestedWeights,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["mode-overrides"] });
      toast.success(data.message || "Weights applied");
    },
    onError: () => toast.error("Apply failed — run analysis first"),
  });
  const resetOverrides = useMutation({
    mutationFn: api.resetModeOverrides,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mode-overrides"] });
      toast.success("Overrides cleared");
    },
  });
  const ov = overrides.data || {};

  return (
    <div
      className="border border-white/10 bg-[#0d0d0d]"
      data-testid="winner-profile-panel"
    >
      <div className="px-3 py-2 border-b border-white/10 flex items-center gap-3 flex-wrap">
        <Robot size={14} className="text-[#00FF66]" weight="fill" />
        <span className="font-display text-sm tracking-widest uppercase text-white">
          Winner Profile — What Wins
        </span>
        <span className="font-mono text-[10px] text-white/40">
          Backtests across top pairs · ranks criteria by conditional win-rate edge
        </span>
        <div className="ml-auto flex items-center gap-2">
          <label className="font-mono text-[10px] text-white/50 flex items-center gap-1">
            pairs
            <select
              value={nPairs}
              onChange={(e) => setNPairs(Number(e.target.value))}
              className="bg-black border border-white/10 px-1 text-white font-mono text-[10px]"
              data-testid="winner-npairs"
            >
              {[10, 15, 20, 30].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <label className="font-mono text-[10px] text-white/50 flex items-center gap-1">
            days
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="bg-black border border-white/10 px-1 text-white font-mono text-[10px]"
              data-testid="winner-days"
            >
              {[30, 60, 90, 180].map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          <button
            data-testid="winner-run-btn"
            onClick={() => start.mutate()}
            disabled={isRunning || start.isPending}
            className="px-3 py-1 border border-[#00FF66]/50 hover:bg-[#00FF66]/10 text-[#00FF66] font-mono text-[10px] uppercase tracking-widest disabled:opacity-40"
          >
            {isRunning ? "Analyzing…" : "Run Analysis"}
          </button>
        </div>
      </div>

      {jobStatus === "error" && (
        <div className="px-3 py-6 text-center font-mono text-[11px] text-[#FF3B30]">
          Analysis failed: {status.data?.error || "unknown error"}. Click{" "}
          <span className="text-[#00FF66]">Run Analysis</span> to retry.
        </div>
      )}

      {!result && !isRunning && jobStatus !== "error" && (
        <div className="px-3 py-8 text-center text-white/40 font-mono text-[11px]">
          Click <span className="text-[#00FF66]">Run Analysis</span> to backtest
          across the top {nPairs} pairs over {days} days.
          <br />
          Runs in the background (2-5 minutes). Every criterion is ranked by
          conditional win-rate edge so you see exactly what patterns make trades win.
        </div>
      )}

      {isRunning && (
        <div className="px-3 py-8 text-center text-white/60 font-mono text-[11px] animate-pulse">
          Running backtest scan · {nPairs} pairs × 4 modes × 2 directions… auto-polling every 5s.
        </div>
      )}

      {result?.per_mode && (
        <div className="p-3 space-y-3">
          <div className="grid grid-cols-4 gap-2">
            {MODES.map((m) => {
              const s = result.per_mode[m];
              const wr = s?.win_rate || 0;
              const color =
                wr >= 65
                  ? "#00FF66"
                  : wr >= 55
                    ? "#8DD35B"
                    : wr >= 45
                      ? "#F5C518"
                      : "#FF3B30";
              return (
                <div
                  key={m}
                  className="border border-white/10 p-2"
                  data-testid={`winner-mode-${m}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                    {m}
                  </div>
                  <div
                    className="font-display text-2xl font-bold"
                    style={{ color }}
                  >
                    {wr.toFixed(1)}%
                  </div>
                  <div className="font-mono text-[10px] text-white/40">
                    {s.wins}W · {s.losses}L · {s.trades}T
                  </div>
                  <div className="font-mono text-[9px] text-white/30 mt-0.5">
                    {s.avg_trades_per_pair}/pair
                  </div>
                </div>
              );
            })}
          </div>

          {result.recommendations?.length > 0 && (
            <div className="border border-white/10 p-2 text-[11px] font-mono text-white/70">
              <div className="uppercase tracking-widest text-white/50 mb-1">
                Recommendations
              </div>
              {result.recommendations.map((r, i) => (
                <div key={i} className="text-white/80 py-0.5">
                  • {r}
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {MODES.map((m) => {
              const s = result.per_mode[m];
              if (!s || !s.criteria?.length) return null;
              const top = s.criteria.slice(0, 6);
              return (
                <div key={m} className="border border-white/10">
                  <div className="px-2 py-1 border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-white/60">
                    {m} — top criteria
                  </div>
                  <table className="w-full text-[10px] font-mono">
                    <thead className="text-white/40">
                      <tr className="border-b border-white/5">
                        <th className="text-left px-2 py-1">Criterion</th>
                        <th className="text-right px-1">Hit WR</th>
                        <th className="text-right px-1">Miss WR</th>
                        <th className="text-right px-1">Edge</th>
                        <th className="text-center px-1">Bar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {top.map((c) => {
                        const isPos = c.edge_pp >= 0;
                        return (
                          <tr key={c.criterion} className="border-b border-white/5">
                            <td className="px-2 py-1 text-white/90">
                              {isPos ? (
                                <TrendUp
                                  size={9}
                                  className="inline text-[#00FF66] mr-1"
                                />
                              ) : (
                                <TrendDown
                                  size={9}
                                  className="inline text-[#FF3B30] mr-1"
                                />
                              )}
                              {c.criterion.replace(/_/g, " ")}
                            </td>
                            <td className="text-right px-1 text-white/70">
                              {c.hit_win_rate}%
                            </td>
                            <td className="text-right px-1 text-white/40">
                              {c.miss_win_rate}%
                            </td>
                            <td
                              className="text-right px-1 font-bold"
                              style={{ color: isPos ? "#00FF66" : "#FF3B30" }}
                            >
                              {isPos ? "+" : ""}
                              {c.edge_pp}pp
                            </td>
                            <td className="px-1 py-1">
                              <EdgeBar pp={c.edge_pp} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
